/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { AccessionPrefix } from './AccessionPrefix';
import type { AntibodySpecies } from './AntibodySpecies';
import type { AntigenRetrievalMethod } from './AntigenRetrievalMethod';
import type { Block } from './Block';
import type { Lab } from './Lab';
import type { LabOrder } from './LabOrder';
import type { LabStation } from './LabStation';
import type { SlideClassificationSimple } from './SlideClassificationSimple';
import type { SlideDZI } from './SlideDZI';
import type { SlideFlag } from './SlideFlag';
import type { SlideMetadata } from './SlideMetadata';
import type { Specimen } from './Specimen';

export type Slide = {
    readonly id?: number;
    /**
     * unique identifier for the slide
     */
    uuid?: string;
    readonly case_id?: number;
    readonly lab_id?: number;
    /**
     * original filename of the slide from the scanner
     */
    name?: string | null;
    readonly created_at?: string;
    /**
     * date slide was scanned by lab, retrieved from slide metadata
     */
    scanned_date?: string | null;
    /**
     * the name of the process that added the slide
     */
    inserted_by?: Slide.inserted_by;
    /**
     * name of the file stored in S3 generated by the host scanner
     */
    url_path?: string | null;
    /**
     * an md5 hash of the uplaoded s3 filename
     */
    uploader_hash?: string | null;
    /**
     * status of a slide until it's fully imported and visible to a user in 'success'. History logged by SlideStatusChange.
     */
    status?: Slide.status;
    /**
     * industry name for the extracted 'case id' value. Should match Case.accession
     */
    accession_number?: string | null;
    /**
     * slide number in the provided block
     */
    slide_number?: string | null;
    readonly stain_id?: number;
    readonly mpp?: number;
    readonly slide_rotation_degrees?: number;
    /**
     * flag for dermpaths to identify a true, false, unknown diagnostic for later AI training
     */
    manual_diagnostic_flag?: boolean | null;
    readonly show_diagnostic_flag?: boolean;
    /**
     * Whether the slide files are available in S3 or have been removed/archived.
     */
    available_in_storage?: boolean;
    readonly low_power_dzi?: SlideDZI;
    readonly high_power_dzi_url?: string;
    /**
     * Full URL in cloudfront where the slide's full (0-40x+) pre-computed tile "manifest" has been stored. If this is populated the viewer should load all of a slide's tiles from paths provided via the manifest file, indicating no tile server use.
     */
    full_precompute_location?: string | null;
    readonly specimen?: Specimen;
    readonly specimen_id?: number;
    readonly block_obj?: Block;
    readonly block_obj_id?: number;
    readonly classifications?: Array<SlideClassificationSimple>;
    readonly wpl_case_link?: string;
    readonly was_previously_viewed?: boolean;
    readonly accession_prefix?: AccessionPrefix;
    /**
     * Date upon which the slide's WSI will be removed and tiles will be used exclusively.
     */
    high_res_removal_date?: string | null;
    readonly lab_orders?: Array<LabOrder>;
    readonly lab_order?: LabOrder;
    readonly lab_order_id?: number;
    readonly lab_station?: LabStation;
    lis_state?: Slide.lis_state | null;
    flags: Array<SlideFlag>;
    is_rescan?: boolean;
    is_control?: boolean;
    metadata: SlideMetadata;
    readonly lab?: Lab;
    /**
     * type of slide: brightfield or fluorescence
     */
    slide_type?: Slide.slide_type;
    readonly antigen_retrieval_method?: AntigenRetrievalMethod;
    readonly antibody_species?: AntibodySpecies;
}

export namespace Slide {

    /**
     * the name of the process that added the slide
     */
    export enum inserted_by {
        UPLOADER = 'uploader',
        WPL_IMPORT = 'WPL Import',
        SONIC_IMPORT = 'Sonic Import',
        SCAN_AND_IMPORT_SLIDES = 'scan_and_import_slides',
        WSI_IMPORTER_SCRIPT = 'wsi_importer_script',
        LIS_IMPORT = 'LIS Import',
        SEED_COMMAND = 'Seed Command',
    }

    /**
     * status of a slide until it's fully imported and visible to a user in 'success'. History logged by SlideStatusChange.
     */
    export enum status {
        RECEIVED_WPL = 'received-wpl',
        RECEIVED_LAB = 'received-lab',
        PENDING = 'pending',
        PRECOMPUTING = 'precomputing',
        UPLOADED = 'uploaded',
        ERROR = 'error',
        MANUAL_MERGE = 'manual-merge',
        MANUAL_REVIEW = 'manual-review',
        SUCCESS = 'success',
        PENDING_AI = 'pending-ai',
        DELETED = 'deleted',
        ARCHIVED = 'archived',
        ON_HOLD = 'on-hold',
        PROCESSING = 'processing',
    }

    export enum lis_state {
        UNSTAINED = 'unstained',
        RACKED = 'racked',
        STAINED = 'stained',
        SCANNING = 'scanning',
        SCANNED = 'scanned',
    }

    /**
     * type of slide: brightfield or fluorescence
     */
    export enum slide_type {
        BRIGHTFIELD = 'brightfield',
        FLUORESCENCE = 'fluorescence',
    }


}
